import { MetaDefinition } from '@angular/platform-browser';

export type LandingSeoPage = 'main' | 'shippers' | 'drivers';

export const LANDING_SITE_NAME = 'FR8BOT';

/** Paths must match `apps/landing/public/sitemap.xml`; images are 2:1-ish, as link previews crop. */
export const LANDING_SEO_PAGES: Record<LandingSeoPage, { path: string; image: string }> = {
  main: { path: '/', image: '/assets/image/web-main-home-video.webp' },
  shippers: { path: '/shippers', image: '/assets/image/web-main-home-video.webp' },
  drivers: { path: '/drivers', image: '/assets/image/web-carrier-video.webp' },
};

/** Every meta tag the landing writes, so leaving a landing page can take them all back. */
export const LANDING_SEO_META_SELECTORS = [
  "name='description'",
  "property='og:title'",
  "property='og:description'",
  "property='og:url'",
  "property='og:type'",
  "property='og:site_name'",
  "property='og:image'",
  "name='twitter:card'",
  "name='twitter:title'",
  "name='twitter:description'",
  "name='twitter:image'",
];

export interface LandingSeoTexts {
  title: string;
  description: string;
}

export interface LandingSeoTags {
  title: string;
  canonicalUrl: string;
  meta: MetaDefinition[];
}

export function landingSeoKeys(page: LandingSeoPage): LandingSeoTexts {
  return { title: `landing.seo.${page}.title`, description: `landing.seo.${page}.description` };
}

/**
 * The head of one landing page. A text that is still empty in i18n falls back to the site name
 * (title) or is left out (description): an empty `description` tag makes search engines show a
 * blank snippet instead of picking one from the page.
 */
export function buildLandingSeoTags(
  page: LandingSeoPage,
  texts: LandingSeoTexts,
  origin: string,
): LandingSeoTags {
  const { path, image } = LANDING_SEO_PAGES[page];
  const title = texts.title.trim() || LANDING_SITE_NAME;
  const description = texts.description.trim();
  const canonicalUrl = origin + path;
  const imageUrl = origin + image;

  const meta: MetaDefinition[] = [
    { property: 'og:title', content: title },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: LANDING_SITE_NAME },
    { property: 'og:image', content: imageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:image', content: imageUrl },
  ];

  if (description) {
    meta.push(
      { name: 'description', content: description },
      { property: 'og:description', content: description },
      { name: 'twitter:description', content: description },
    );
  }

  return { title, canonicalUrl, meta };
}
